import { format, parseISO } from 'date-fns';
import ru from 'date-fns/locale/ru';
import { useEffect, useMemo, useState } from 'react';
import Tooltip from '@mui/material/Tooltip';
import { useProgramHistoryPageRedirect } from '@pages/programs/history/helpers.ts';
import { Header } from '@widgets/header/index.ts';
import { ProgramListFilter } from '@widgets/programs/index.ts';
import { InfinityList } from '@widgets/shared/InfinityList/index.tsx';
import { BodyWithFilterAndList } from '@features/common/BodyWithFilterAndList/index.tsx';
import { Search, useFilter } from '@features/common/index.ts';
import { HistoryGroupedPrograms, ProgramHistoryList } from '@features/programs/index.ts';
import * as st from '@features/programs/listBody/styles.module.scss';
import { ArrowShortLeft, FilterIcon, Search as SearchIcon } from '@shared/assets/index.ts';
import { useMobile } from '@shared/hooks/index.ts';
import { useModuleByCodeParam } from '@shared/hooks/use-module-by-code-param.ts';
import { useSaveScroll } from '@shared/hooks/use-save-scroll.ts';
import { FilterMobileActions } from '@shared/ui/filter-mobile-actions/index.ts';
import { IconButton, PageHead } from '@shared/ui/index.ts';
import { SkeletonList } from '@shared/ui/skeleton-list/index.ts';
import { useProgramsHistoryInfiniteQuery } from '@src/api/api-hooks/programs/useProgramsHistoryInfiniteQuery.ts';
import { Blank } from '@src/library/components/common/index.ts';

export const ProgramHistoryPage = () => {
  const { setListScrollData } = useSaveScroll('VideoProgramsHistoryList');
  const { doRedirectOnPrevPage } = useProgramHistoryPageRedirect();
  const { isMobile } = useMobile();
  const {
    tags,
    selectedTags,
    onTagChange,
    search,
    onSearch,
    selectedAuthors,
    onAuthorChange,
    isSearchMobile,
    setIsSearchMobile,
    selectedPartitions,
    onPartitionsChange,
    handleResetFilter,
    isFilterActive,
    partitions,
    authors,
  } = useFilter();

  const { module, moduleId } = useModuleByCodeParam();

  const filterParams = useMemo(
    () => ({
      module: moduleId,
      partitions: selectedPartitions.length ? selectedPartitions.join(',') : undefined,
      tags: selectedTags.length ? selectedTags.join(',') : undefined,
      authors: selectedAuthors.length ? selectedAuthors.join(',') : undefined,
      search: search.length ? search : undefined,
    }),
    [selectedPartitions, search, selectedAuthors, selectedTags, moduleId]
  );

  const {
    data: programs,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useProgramsHistoryInfiniteQuery(filterParams);

  const [isFilterOpen, setIsFilterOpen] = useState(() => {
    if (isMobile) {
      return false;
    }
    return isFilterActive;
  });

  const IsActiveFilterBtn = useMemo(
    () => Boolean(isFilterOpen || isFilterActive),
    [isFilterOpen, isFilterActive]
  );

  useEffect(() => {
    if (!isMobile && !isFilterOpen && isFilterActive) {
      setIsFilterOpen(true);
    }
  }, [isMobile, isFilterOpen, setIsFilterOpen, isFilterActive]);

  const [mobileWithFilterOn, setMobileWithFilterOn] = useState(false);

  const groupedPrograms = useMemo(() => {
    if (programs?.results) {
      const programsWithParsedDate = programs.results.map((p, index) => {
        return {
          ...p,
          dateWithoutTimeInformation: format(parseISO(p.viewed_at), 'dd.MM.yyyy', {
            locale: ru,
          }),
          elementIndex: index,
        };
      });
      return Object.groupBy(
        programsWithParsedDate,
        (item) => item.dateWithoutTimeInformation
      ) as HistoryGroupedPrograms;
    }
    return {} as HistoryGroupedPrograms;
  }, [programs]);

  const programsTitles = useMemo(() => {
    return programs?.results.map((p) => p.title) || [];
  }, [programs]);

  return (
    <>
      <Header
        MCenter={<>{mobileWithFilterOn ? 'Фильтр' : module?.verbose_name}</>}
        menuClassName={`${mobileWithFilterOn ? st.hide : ''}`}
      />
      <PageHead
        Center={'История просмотров'}
        Left={
          <IconButton
            variant="dark"
            onClick={() => {
              doRedirectOnPrevPage('/programs/library/');
            }}
          >
            <ArrowShortLeft />
          </IconButton>
        }
        Right={
          <>
            {mobileWithFilterOn ? (
              <FilterMobileActions
                isFilterActive={isFilterActive}
                onReset={handleResetFilter}
                onConfirm={() => {
                  setMobileWithFilterOn(false);
                  setIsFilterOpen((prev) => !prev);
                }}
              />
            ) : (
              <>
                {isMobile ? (
                  <IconButton variant="mobile" onClick={() => setIsSearchMobile(!isSearchMobile)}>
                    <SearchIcon />
                  </IconButton>
                ) : (
                  <Search
                    isOpenInit={search !== ''}
                    search={search}
                    onSearch={onSearch}
                    titles={programsTitles}
                  />
                )}

                <Tooltip title="Фильтр">
                  <IconButton
                    variant={`${IsActiveFilterBtn ? 'activeDark' : 'dark'}`}
                    onClick={() => {
                      isFilterOpen && handleResetFilter();
                      isMobile && setMobileWithFilterOn(true);
                      setIsFilterOpen((prev) => !prev);
                    }}
                  >
                    <FilterIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </>
        }
      />
      {isSearchMobile && (
        <>
          <Header MCenter={<>Поиск</>} isHiddenHamburger={isSearchMobile} />
          <PageHead
            Left={
              <IconButton
                variant="dark"
                onClick={() => {
                  setIsSearchMobile(!isSearchMobile);
                  onSearch('');
                }}
              >
                <ArrowShortLeft />
              </IconButton>
            }
            Right={
              <>
                <Search
                  mobile
                  isOpenInit={search !== ''}
                  search={search}
                  onSearch={onSearch}
                  titles={programsTitles}
                />
              </>
            }
          />
        </>
      )}

      <BodyWithFilterAndList
        isFilterOpen={isFilterOpen}
        Filter={
          <ProgramListFilter
            partitions={partitions}
            authors={authors}
            selectedPartitions={selectedPartitions}
            onAuthorChange={onAuthorChange}
            onTagChange={onTagChange}
            isOpen={isFilterOpen}
            tags={tags}
            activeTab={'all'}
            handleResetFilter={handleResetFilter}
            hasResetButton={!mobileWithFilterOn && isFilterActive}
            selectedTags={selectedTags}
            selectedAuthors={selectedAuthors}
            onPartitionChange={(id) => onPartitionsChange([id])}
          />
        }
        List={
          <InfinityList loading={isFetchingNextPage} loadMore={fetchNextPage} hasMore={hasNextPage}>
            {isLoading ? (
              <SkeletonList />
            ) : (
              <ProgramHistoryList
                programsListLength={programsTitles.length}
                groupedPrograms={groupedPrograms}
                onProgramClick={() => {
                  setListScrollData({
                    scrollY: window.scrollY,
                  });
                }}
              />
            )}
          </InfinityList>
        }
        EmptyBlank={
          <Blank
            text={
              Object.keys(filterParams).length
                ? 'По вашему запросу ничего не найдено'
                : 'В этом списке ничего нет'
            }
          />
        }
        isShowBlank={programsTitles.length === 0 && !isLoading}
      />
    </>
  );
};
